import { type SerializedError } from '@reduxjs/toolkit';
import { useCallback, useMemo, useState } from 'react';
import { SDKQueryErrorType, SearchResults } from 'types/api';

type PaginationArgs = {
  orgId: string;
  startIndex: number;
  count: number;
};

type UseRTKQueryPaginationResult<T> = {
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  hasNextPage: boolean;
  currentIndex: number;
  totalPages: number;
  loadNextPage: () => void;
  refetch: () => void;
  loadFirstPage: () => void;
};

export type RTKQueryResponse<Result> = {
  data?: SearchResults<Result>;
  error?: SDKQueryErrorType | SerializedError;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export type RTKQueryHook<Result> = (
  args: {
    orgId: string;
    startIndex: number;
    count: number;
  },
  { skip, refetchOnMountOrArgChange }: { skip: boolean; refetchOnMountOrArgChange: boolean }
) => RTKQueryResponse<Result>;

export function useRTKQueryPagination<Result>(
  useQueryHook: RTKQueryHook<Result>,
  baseArgs: PaginationArgs
): UseRTKQueryPaginationResult<Result> {
  const { startIndex = 1, count = 5 } = baseArgs;
  // Track current page in component state or use a ref
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  // Build query args with current page
  const queryArgs = useMemo(
    () => ({
      ...{
        ...baseArgs,
        startIndex: currentIndex,
        count: count,
      },
    }),
    [baseArgs, count, currentIndex]
  );

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQueryHook(queryArgs, { skip: false, refetchOnMountOrArgChange: true });

  const data = useMemo(() => response?.results || [], [response]);
  const totalPages = useMemo(() => {
    if (!response?.metadata?.totalHits) return 0;
    return Math.ceil(response.metadata.totalHits / count);
  }, [response?.metadata?.totalHits, count]);
  const hasNextPage = useMemo(
    () =>
      (response?.metadata?.totalHits && response.metadata.totalHits > currentIndex - 1 + count) ||
      false,
    [response?.metadata.totalHits, currentIndex, count]
  );

  const loadNextPage = useCallback(
    (index?: number) => {
      if (index) {
        setCurrentIndex(index);
      } else if (hasNextPage && !isFetching) {
        setCurrentIndex((prev: number) => prev + count);
      }
    },
    [count, hasNextPage, isFetching]
  );

  const refetchFirstPage = useCallback(() => {
    loadNextPage(startIndex);
  }, [loadNextPage, startIndex]);

  return {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    currentIndex,
    totalPages,
    loadNextPage,
    refetch,
    loadFirstPage: refetchFirstPage,
  };
}
